"use client";

// WO-GF-001-P1B-S3 · Item 1 — CHROME LONG-TAIL. The real chrome (sign-in popup W6/C4, change-mountain
// dropdown S1/C5, cookie-consent C8) was live on only ~39 of 402 pages; on the ~363 mountain SUB-pages the
// captured hub header is JS-dead (R6-6). This layer makes it real WITHOUT swapping the header wholesale:
// same document-scoped pattern as MarketplaceCartLayer / ScheduleMapLayer / TransportSearchLayer — it finds
// the CAPTURED (dead) hub-header markup and wires the real handlers onto it, injecting nothing that shows at
// rest, so the pixel gate stays green (byte-identical capture; the injected change-mountain dropdown is
// display:none until opened, exactly like the real SiteHeaderHub's). Desktop-only (F30: mobile stays mirror
// on ported pages). F26 is respected: the captured header keeps its hidden `resort-sitemap` SEO nav (the layer
// never touches it), and the multi-variant header markup is left byte-for-byte intact.
//
// It renders <LoginPopup/> itself so every touched page has the popup the sign-in trigger opens (on the ~39
// already-real pages the header component mounts LoginPopup; here the layer is that mount). Idempotent + fully
// cleaned up on unmount. Behavior gates: chrome/signin-popup-authenticates + chrome/nav-dropdown-opens.

import { useEffect } from "react";
import { useCartStore } from "@/lib/cart-store";
import { hudNavHref, mountainCodeFromPath } from "./hub-nav";
import LoginPopup from "./LoginPopup";
import { openMountainSelect } from "./MountainSelectLayer";

// Marks the injected change-mountain menu so it is only built once per header and torn down cleanly.
const MENU_FLAG = "data-hub-chrome-menu";

export default function HubChromeLayer() {
  useEffect(() => {
    const cleanups: Array<() => void> = [];

    // ---- C4 · SIGN-IN (W6) — the captured hub header's profile button opens the login popup. ----------
    // Captured markup: `<div class="css-ss5hdk"><button class="…css-1deacqj"><img alt="Profile" …></button></div>`.
    // Match by the profile image so it works across the (rare) class drift between captures; walk to the
    // clickable ancestor. Tag it data-behavior="signin-open" so behavior.mjs' chrome check finds the trigger.
    const signInButtons = new Set<HTMLElement>();
    for (const img of Array.from(
      document.querySelectorAll<HTMLImageElement>('header img[alt="Profile"]'),
    )) {
      // ACCOUNT-MENU-SINGLE-20260925 — the design export's own avatar (data-sg-menu="user") opens the Kit's
      // account menu; it is never a captured hub trigger, so the host popup must not attach to it.
      if (img.closest('[data-sg-menu="user"]')) continue;
      const btn = img.closest<HTMLElement>("button, [role='button'], a, .css-ss5hdk");
      if (btn && !btn.closest('[data-sg-menu="user"]')) signInButtons.add(btn);
    }
    const openAuth = (e: Event) => {
      e.preventDefault();
      useCartStore.getState().setAuthOpen(true);
    };
    for (const btn of signInButtons) {
      btn.setAttribute("data-behavior", "signin-open");
      btn.style.cursor = "pointer";
      btn.addEventListener("click", openAuth);
      cleanups.push(() => btn.removeEventListener("click", openAuth));
    }

    // ---- C5 · CHANGE-MOUNTAIN — the captured hub trigger (css-opckaj) on the ~361 sub-pages.
    //
    // S26 · A3 — it now opens the ONE site-wide R1a selector (`MountainSelectLayer`) instead of a
    // locally-injected 30-row dropdown. The S26 · A2 mount audit found THREE divergent mountain
    // selectors still live after S25 rebuilt the R1a one, which is why the founder's walk hit an
    // unchanged selector; a per-surface injection is exactly how that divergence happened, so the
    // injection is gone rather than re-synced.
    //
    // What the deleted dropdown carried, and where it went:
    //   • S12 · RW2-5 body-portal + fixed anchoring (the "clipped/hidden" fix — the menu was 320px tall
    //     inside two overflow:hidden ancestors, so ~7px of it survived). The modal is a full-viewport
    //     fixed overlay: no ancestor can clip it and there is no anchor to recompute on scroll/resize.
    //   • S23 · F125 active-product-first ordering and S15 · F89 R1 hub routing — both now live in
    //     `MountainSelectModal` (resortsActiveFirst + resortHubHref), so they are stated once instead of
    //     re-implemented per header.
    // Still nothing at rest → the at-rest pixel baselines on all 361 sub-pages are untouched.
    for (const trigger of Array.from(
      document.querySelectorAll<HTMLElement>("header .css-opckaj"),
    )) {
      if (trigger.getAttribute(MENU_FLAG)) continue; // already wired (idempotent)
      trigger.setAttribute(MENU_FLAG, "true");

      trigger.setAttribute("data-behavior", "nav-toggle");
      trigger.setAttribute("role", "button");
      trigger.setAttribute("aria-label", "Change mountain");
      // Founder QA 2026-09-24 — this badge opens the integrated selector dialog directly.
      trigger.setAttribute("aria-haspopup", "dialog");
      trigger.style.cursor = "pointer";

      const openSelect = (e: Event) => {
        e.preventDefault();
        // Neutral entry point → no mode → the selected mountain's hub.
        openMountainSelect(null);
      };
      trigger.addEventListener("click", openSelect);
      cleanups.push(() => {
        trigger.removeEventListener("click", openSelect);
        trigger.removeAttribute(MENU_FLAG); // allow a clean re-wire after unmount
      });
    }

    // ---- W5/P0 · HUD NAV — the captured hub HUD nav buttons (MARKETPLACE / TRANSPORT / DIRECTORY) navigate
    // to the real per-mountain pages (dead photocopies in the mirror — the founder's #1). Destinations resolve
    // through the shared `hudNavHref` map (live-derived): marketplace→/deals-and-packages, transport→
    // /bus-schedule, directory→/profile — each a DISTINCT served page for every mountain. flights/stays carry
    // no href (Coming Soon) → left inert exactly as live. Matched by the captured `<p>` label; nothing shows at
    // rest (the button markup is untouched → pixel gate holds). Tagged data-behavior="hud-nav" for the gate.
    const hudCode = mountainCodeFromPath(location.pathname);
    if (hudCode) {
      for (const btn of Array.from(
        document.querySelectorAll<HTMLElement>("header button"),
      )) {
        if (btn.hasAttribute("disabled")) continue; // flights/stays "Coming Soon"
        const label = (btn.querySelector("p")?.textContent || "").trim();
        const href = hudNavHref(hudCode, label);
        if (!href) continue; // not a HUD nav item (cart/sign-in/etc.)
        btn.setAttribute("data-behavior", "hud-nav");
        btn.style.cursor = "pointer";
        const go = (e: Event) => {
          e.preventDefault();
          window.location.assign(href);
        };
        btn.addEventListener("click", go);
        cleanups.push(() => btn.removeEventListener("click", go));
      }
    }

    // ---- C8 · COOKIE-CONSENT — the captured react-cookie-consent banner's Accept button (dead in the
    // mirror) sets the consent cookie and dismisses the banner. Only wired on click; nothing is hidden at
    // rest, so a fresh gate capture still shows the banner in both live + build (pixel gate untouched).
    for (const accept of Array.from(
      document.querySelectorAll<HTMLElement>("#rcc-confirm-button"),
    )) {
      const onAccept = () => {
        document.cookie = "CookieConsent=true; path=/; max-age=31536000; samesite=lax";
        const banner = accept.closest<HTMLElement>(".CookieConsent");
        if (banner) banner.style.display = "none";
      };
      accept.style.cursor = "pointer";
      accept.addEventListener("click", onAccept);
      cleanups.push(() => accept.removeEventListener("click", onAccept));
    }

    return () => cleanups.forEach((c) => c());
  }, []);

  // The popup the sign-in trigger opens (shared cart-store session — one auth for chrome + cart).
  return <LoginPopup />;
}
